/**
 * Keyboard Shortcuts Hook
 *
 * Provides a hook for registering keyboard shortcuts with proper
 * handling for input elements and modifier keys.
 */

import { useEffect, useCallback, useRef } from 'react';

/**
 * Represents a keyboard shortcut definition
 */
export interface Shortcut {
  /** Unique key for the shortcut */
  key: string;
  /** Human-readable description */
  description: string;
  /** Category for grouping in help modal */
  category: 'global' | 'navigation' | 'actions' | 'editing';
  /** Whether Ctrl/Cmd modifier is required */
  ctrlOrCmd?: boolean;
  /** Whether Shift modifier is required */
  shift?: boolean;
  /** Whether Alt modifier is required */
  alt?: boolean;
  /** Callback to execute when shortcut is triggered */
  action: () => void;
  /** Whether shortcut should work when focused on an input */
  allowInInput?: boolean;
}

export type ShortcutMap = Record<string, Shortcut>;

/**
 * Check if the current platform is Mac
 */
export const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);

/**
 * Get the display string for a shortcut (e.g., "Cmd+K" or "Ctrl+K")
 */
export function getShortcutDisplay(shortcut: Shortcut): string {
  const parts: string[] = [];

  if (shortcut.ctrlOrCmd) {
    parts.push(isMac ? '⌘' : 'Ctrl');
  }
  if (shortcut.alt) {
    parts.push(isMac ? '⌥' : 'Alt');
  }
  if (shortcut.shift) {
    parts.push('⇧');
  }

  // Format the key for display
  let keyDisplay = shortcut.key.toUpperCase();
  if (shortcut.key === 'Escape') keyDisplay = 'Esc';
  if (shortcut.key === 'ArrowUp') keyDisplay = '↑';
  if (shortcut.key === 'ArrowDown') keyDisplay = '↓';
  if (shortcut.key === 'ArrowLeft') keyDisplay = '←';
  if (shortcut.key === 'ArrowRight') keyDisplay = '→';
  if (shortcut.key === ' ') keyDisplay = 'Space';
  if (shortcut.key === 'Enter') keyDisplay = '↵';

  parts.push(keyDisplay);

  return parts.join(isMac ? '' : '+');
}

/**
 * Check if an element is an input-like element
 */
function isInputElement(element: EventTarget | null): boolean {
  if (!element || !(element instanceof HTMLElement)) return false;

  const tagName = element.tagName.toLowerCase();
  const isInput = tagName === 'input' || tagName === 'textarea' || tagName === 'select';
  const isContentEditable = element.isContentEditable;

  return isInput || isContentEditable;
}

/**
 * Hook to register and handle keyboard shortcuts
 *
 * @param shortcuts - Map of shortcuts to register
 * @param enabled - Whether shortcuts are currently enabled (default: true)
 */
export function useKeyboardShortcuts(
  shortcuts: ShortcutMap,
  enabled: boolean = true
): void {
  const shortcutsRef = useRef(shortcuts);

  // Keep shortcuts ref updated
  useEffect(() => {
    shortcutsRef.current = shortcuts;
  }, [shortcuts]);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!enabled) return;

    const currentShortcuts = shortcutsRef.current;
    const isInInput = isInputElement(event.target);

    // Check each shortcut
    for (const shortcut of Object.values(currentShortcuts)) {
      // Skip if in input and shortcut doesn't allow it
      if (isInInput && !shortcut.allowInInput) continue;

      // Check if the key matches (case-insensitive)
      const keyMatches = event.key.toLowerCase() === shortcut.key.toLowerCase() ||
        event.code === shortcut.key ||
        event.key === shortcut.key;

      if (!keyMatches) continue;

      // Check modifier keys
      const ctrlOrCmdPressed = isMac ? event.metaKey : event.ctrlKey;
      const ctrlOrCmdRequired = shortcut.ctrlOrCmd ?? false;
      const shiftRequired = shortcut.shift ?? false;
      const altRequired = shortcut.alt ?? false;

      if (ctrlOrCmdRequired !== ctrlOrCmdPressed) continue;
      if (shiftRequired !== event.shiftKey) continue;
      if (altRequired !== event.altKey) continue;

      // Prevent default and execute action
      event.preventDefault();
      event.stopPropagation();
      shortcut.action();
      return;
    }
  }, [enabled]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
}

/**
 * Default global shortcuts that can be extended
 */
export function createGlobalShortcuts(handlers: {
  onShowHelp?: () => void;
  onSearch?: () => void;
  onSave?: () => void;
  onEscape?: () => void;
  onNavigateTab?: (index: number) => void;
  onNew?: () => void;
  onExport?: () => void;
  onRefresh?: () => void;
}): ShortcutMap {
  const shortcuts: ShortcutMap = {};

  if (handlers.onShowHelp) {
    shortcuts['show-help'] = {
      key: '?',
      description: 'Show keyboard shortcuts',
      category: 'global',
      shift: true,
      action: handlers.onShowHelp,
    };
  }

  if (handlers.onSearch) {
    shortcuts['search'] = {
      key: 'k',
      description: 'Quick search / Command palette',
      category: 'global',
      ctrlOrCmd: true,
      action: handlers.onSearch,
    };
  }

  if (handlers.onSave) {
    shortcuts['save'] = {
      key: 's',
      description: 'Save current work',
      category: 'global',
      ctrlOrCmd: true,
      action: handlers.onSave,
    };
  }

  if (handlers.onEscape) {
    shortcuts['escape'] = {
      key: 'Escape',
      description: 'Close modal / Cancel',
      category: 'global',
      action: handlers.onEscape,
      allowInInput: true,
    };
  }

  if (handlers.onNavigateTab) {
    for (let i = 1; i <= 5; i++) {
      shortcuts[`navigate-tab-${i}`] = {
        key: String(i),
        description: `Navigate to tab ${i}`,
        category: 'navigation',
        action: () => handlers.onNavigateTab!(i),
      };
    }
  }

  if (handlers.onNew) {
    shortcuts['new'] = {
      key: 'n',
      description: 'New item / Import',
      category: 'actions',
      action: handlers.onNew,
    };
  }

  if (handlers.onExport) {
    shortcuts['export'] = {
      key: 'e',
      description: 'Export data',
      category: 'actions',
      action: handlers.onExport,
    };
  }

  if (handlers.onRefresh) {
    shortcuts['refresh'] = {
      key: 'r',
      description: 'Refresh / Recalculate',
      category: 'actions',
      action: handlers.onRefresh,
    };
  }

  return shortcuts;
}
